import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  Brain,
  UtensilsCrossed,
  FileText,
  Droplet,
  Salad,
  HeartHandshake,
  ShieldCheck,
  Send,
  Loader2,
  Sparkles,
  Lock,
  Trash2,
  Activity,
  AlertOctagon,
  Info,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_ACTIONS = [
  { icon: UtensilsCrossed, label: "Ask about nutrition", prompt: "What would be a balanced Indian dinner for me tonight?" },
  { icon: FileText, label: "Explain my report", prompt: "Can you explain what my doctor's report means in simple words?" },
  { icon: Droplet, label: "Review my glucose", prompt: "How are my recent glucose readings looking? What patterns do you notice?" },
  { icon: Salad, label: "Plan my meals", prompt: "Can you suggest a simple one-day Indian meal plan for me?" },
  { icon: HeartHandshake, label: "Wellness support", prompt: "I've been feeling stressed lately. What gentle things might help?" },
  { icon: ShieldCheck, label: "Prevention", prompt: "What preventive health habits matter most for someone like me?" },
];

const TIER_META = {
  LOW_RISK: { label: "General", cls: "text-zinc-400 border-white/15 bg-white/5" },
  EDUCATIONAL: { label: "Educational", cls: "text-cyan-300 border-cyan-400/30 bg-cyan-500/10" },
  PERSONALIZED: { label: "Personalized · non-diagnostic", cls: "text-emerald-300 border-emerald-400/30 bg-emerald-500/10" },
  ATTENTION: { label: "Discuss with a professional", cls: "text-amber-300 border-amber-400/30 bg-amber-500/10" },
  URGENT: { label: "Urgent", cls: "text-rose-300 border-rose-400/40 bg-rose-500/10" },
};

const DOMAIN_LABEL = {
  NUTRITION: "Nutrition",
  MEDICAL_REPORT: "Report intelligence",
  GLUCOSE: "Glucose",
  FITNESS: "Fitness",
  WELLNESS: "Wellness",
  PREVENTIVE_HEALTH: "Prevention",
  GENERAL_HEALTH: "General health",
  URGENT_SAFETY: "Safety",
  OTHER: "SilentScan AI",
};

// Render the AI's ###-headings / * bullets as light structure.
const RichText = ({ text }) => {
  const lines = String(text || "").split("\n");
  return (
    <div className="space-y-1.5">
      {lines.map((ln, i) => {
        const t = ln.trim();
        if (!t) return <div key={i} className="h-1.5" />;
        if (t.startsWith("###")) {
          return (
            <div key={i} className="pt-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
              {t.replace(/^#+\s*/, "")}
            </div>
          );
        }
        if (t.startsWith("*") || t.startsWith("-")) {
          return (
            <div key={i} className="flex items-start gap-2 text-sm text-zinc-200 leading-relaxed">
              <span className="text-cyan-400 mt-1.5 text-[8px]">●</span>
              <span dangerouslySetInnerHTML={{ __html: boldify(t.replace(/^[*-]\s*/, "")) }} />
            </div>
          );
        }
        return (
          <p key={i} className="text-sm text-zinc-200 leading-relaxed" dangerouslySetInnerHTML={{ __html: boldify(t) }} />
        );
      })}
    </div>
  );
};

// **bold** → <strong> (model output is sanitized: only this transform is applied)
const boldify = (s) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong class='text-white font-semibold'>$1</strong>");

export default function HealthAI() {
  const { user, openAuth } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [overview, setOverview] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Health Intelligence — SilentScan AI";
  }, []);

  useEffect(() => {
    if (!user) {
      setOverview(null);
      return;
    }
    axios
      .get(`${API}/health-ai/overview`, { withCredentials: true })
      .then(({ data }) => setOverview(data))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = useCallback(
    async (text) => {
      const msg = (text ?? input).trim();
      if (!msg || busy) return;
      setInput("");
      setMessages((m) => [...m, { role: "user", content: msg }]);
      setBusy(true);
      try {
        const { data } = await axios.post(
          `${API}/health-ai/chat`,
          { message: msg, session_id: sessionRef.current },
          { withCredentials: true }
        );
        sessionRef.current = data.session_id;
        setMessages((m) => [
          ...m,
          { role: "assistant", content: data.reply, domain: data.domain, tier: data.tier, personalized: data.personalized },
        ]);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(detail || "The health AI is unavailable right now. Please try again.");
        setMessages((m) => m.slice(0, -1));
        setInput(msg);
      } finally {
        setBusy(false);
      }
    },
    [input, busy]
  );

  const clearMemory = async () => {
    try {
      await axios.delete(`${API}/health-ai/history`, { withCredentials: true });
      toast.success("Your AI conversation memory was deleted");
      setConfirmClear(false);
    } catch {
      toast.error("Could not clear AI memory");
    }
  };

  const chips = [
    overview?.latest_score != null && { label: "Latest Health Score", value: `${overview.latest_score}/100`, when: overview.latest_score_date },
    overview?.doctor_report && { label: "Doctor report", value: overview.report_condition || "On file", when: null },
    overview?.last_glucose && { label: "Last glucose", value: overview.last_glucose, when: null },
    overview?.plan_versions > 0 && { label: "Guidance plans", value: `${overview.plan_versions} saved`, when: null },
  ].filter(Boolean);

  return (
    <div data-testid="health-ai-page" className="min-h-screen bg-[#05050A] text-white flex flex-col">
      {/* Header */}
      <div className="border-b border-white/5 backdrop-blur-md bg-black/40 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-5 py-3.5 flex items-center justify-between">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Dashboard
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#0B3D91] to-cyan-500 flex items-center justify-center shadow-[0_0_18px_rgba(0,229,255,0.35)]">
              <Brain className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-sm font-display font-semibold leading-none">Health Intelligence</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">by SilentScan AI</div>
            </div>
          </div>
          {user ? (
            <button
              data-testid="clear-ai-memory-btn"
              onClick={() => setConfirmClear(true)}
              className="inline-flex items-center gap-1.5 text-[11px] text-zinc-500 hover:text-rose-300 transition-colors"
            >
              <Trash2 className="w-3 h-3" />
              Clear AI memory
            </button>
          ) : (
            <span className="w-16" />
          )}
        </div>
      </div>

      <div className="flex-1 w-full max-w-4xl mx-auto px-5 py-6 flex flex-col">
        {/* Personalization state */}
        {!user && (
          <div
            data-testid="health-ai-guest-banner"
            className="mb-5 rounded-2xl border border-cyan-400/25 bg-gradient-to-r from-[#0B3D91]/25 to-cyan-500/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3"
          >
            <Lock className="w-5 h-5 text-cyan-300 shrink-0" />
            <p className="flex-1 text-xs sm:text-sm text-zinc-300 leading-relaxed">
              You're in general mode. Sign in free and SilentScan AI can
              reason over your saved scores, glucose readings, reports and
              check-ins — visible only to you.
            </p>
            <button
              data-testid="health-ai-signin-btn"
              onClick={openAuth}
              className="px-5 py-2.5 rounded-full btn-glow text-white text-xs font-semibold shrink-0"
            >
              Sign in free
            </button>
          </div>
        )}

        {/* Real-data overview chips */}
        {user && chips.length > 0 && (
          <div data-testid="health-ai-overview" className="mb-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
            {chips.map((c, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-semibold">{c.label}</div>
                <div className="mt-1 text-sm font-semibold text-cyan-200 truncate">{c.value}</div>
                {c.when && <div className="text-[10px] text-zinc-600">{c.when}</div>}
              </div>
            ))}
          </div>
        )}

        {/* Thread */}
        <div ref={scrollRef} className="flex-1 min-h-[300px] max-h-[56vh] overflow-y-auto space-y-4 pr-1">
          {messages.length === 0 && (
            <div className="pt-6">
              <div className="max-w-xl">
                <div className="text-[11px] uppercase tracking-[0.3em] text-cyan-400 font-semibold">
                  Personal Health Intelligence
                </div>
                <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-tight">
                  Ask anything about your health.
                </h1>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                  Nutrition, reports, glucose, wellness, prevention — one
                  intelligence that understands your context and explains things
                  calmly. Educational guidance, never a diagnosis.
                </p>
              </div>
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {QUICK_ACTIONS.map((q) => (
                  <button
                    key={q.label}
                    data-testid={`quick-${q.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => send(q.prompt)}
                    className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-left hover:border-cyan-400/30 hover:bg-white/[0.05] transition-colors group"
                  >
                    <q.icon className="w-5 h-5 text-cyan-300 group-hover:scale-110 transition-transform" />
                    <div className="mt-2.5 text-sm font-medium text-zinc-100">{q.label}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <AnimatePresence initial={false}>
            {messages.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className={m.role === "user" ? "flex justify-end" : "flex justify-start"}
              >
                {m.role === "user" ? (
                  <div className="max-w-[85%] rounded-2xl rounded-br-md bg-gradient-to-br from-[#0B3D91] to-cyan-600 px-4 py-3 text-sm text-white leading-relaxed">
                    {m.content}
                  </div>
                ) : (
                  <div className="max-w-[92%] rounded-2xl rounded-bl-md border border-white/10 bg-white/[0.04] px-4 py-3.5">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
                        <Sparkles className="w-3 h-3 text-cyan-400" />
                        {DOMAIN_LABEL[m.domain] || "SilentScan AI"}
                      </span>
                      {m.tier && TIER_META[m.tier] && (
                        <span
                          data-testid={`tier-badge-${i}`}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[9px] font-semibold uppercase tracking-widest ${TIER_META[m.tier].cls}`}
                        >
                          {m.tier === "URGENT" ? <AlertOctagon className="w-3 h-3" /> : <Info className="w-3 h-3" />}
                          {TIER_META[m.tier].label}
                        </span>
                      )}
                    </div>
                    <RichText text={m.content} />
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {busy && (
            <div className="flex justify-start" data-testid="health-ai-thinking">
              <div className="rounded-2xl rounded-bl-md border border-white/10 bg-white/[0.04] px-4 py-3.5 flex items-center gap-2.5 text-sm text-zinc-400">
                <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />
                Reasoning through your health context…
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          className="mt-4 mb-20 md:mb-0"
        >
          <div className="flex items-center gap-2 rounded-2xl bg-black/50 border border-white/10 focus-within:border-cyan-400/50 px-4 py-3 transition-colors">
            <input
              data-testid="health-ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              maxLength={3000}
              placeholder="Ask about nutrition, reports, glucose, wellness…"
              className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-zinc-600"
              disabled={busy}
            />
            <button
              data-testid="health-ai-send-btn"
              type="submit"
              disabled={busy || !input.trim()}
              className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0B3D91] to-cyan-500 flex items-center justify-center shadow-[0_0_15px_rgba(0,229,255,0.4)] disabled:opacity-40 transition-all hover:scale-105 shrink-0"
              aria-label="Send"
            >
              {busy ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Send className="w-4 h-4 text-white" />}
            </button>
          </div>
          <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2 text-[10px] text-zinc-600">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3 h-3 text-emerald-500" />
              Educational guidance only — never a diagnosis. Emergencies: call 112 (India).
            </span>
            {user && (
              <Link to="/history" className="inline-flex items-center gap-1 text-zinc-500 hover:text-cyan-300 transition-colors">
                <Activity className="w-3 h-3" />
                What SilentScan AI remembers →
              </Link>
            )}
          </div>
        </form>
      </div>

      {/* Clear memory confirm */}
      <AlertDialog open={confirmClear} onOpenChange={setConfirmClear}>
        <AlertDialogContent data-testid="confirm-clear-memory" className="bg-[#0A0A14] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear AI conversation memory?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This permanently deletes all your SilentScan AI Health
              Intelligence conversations. Your health records (scores, reports,
              readings) are not affected. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-clear-memory-yes"
              onClick={clearMemory}
              className="bg-rose-500 text-white hover:bg-rose-600"
            >
              Delete AI memory
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
